import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PoolAttributes,
  SourcingPurchaseBulkCreateRequest,
  SourcingService,
} from 'api-clients/monolith';
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useToaster } from '@/components/base/toaster';

import { VonqContractPostingRequirements } from 'api-clients/monolith/models/VonqContractPostingRequirements';

import { useBudgetType } from '@/hooks/use-budget-type';
import { useIsFirstJobPostOnAccount } from '@/hooks/use-is-first-job-post-on-account';
import { usePostHog } from '@/hooks/use-posthog';
import { POOL, ZIPRECRUITER } from '@/types/channels';
import { SOURCING_CURRENCY, SOURCING_PAY_TYPE } from '@/utils/sourcing-billing';
import { stripHtmlTags } from '@/utils/string-util';

import {
  ChannelFormState,
  JobPostingForm,
  JobPostingStep,
  PostingResultData,
} from './types';
import { derivePostingResult } from './utils/derive-posting-result';
import { useValidationSchema } from './validation/use-validation-schema';
import {
  LoadedOptions,
  shouldShowField,
} from './job-details/posting-requirements/display-rules';
import { buildLoadedOptionsFromCache } from './job-details/posting-requirements/use-autocomplete-options';

/**
 * Builds the vonq posting requirements payload for a single contract.
 * Excludes fields that are hidden by display_rules and fields with empty values
 * in a single pass, so the API only receives visible, intentional values.
 */
const NON_INPUT_TYPES = new Set(['STATISCH', 'QUESTIONNAIRE']);

function buildVonqPostingRequirementsPayload(
  rawValues: Record<string, string | string[] | null | undefined>,
  contractDef: VonqContractPostingRequirements | undefined,
  loadedOptions: LoadedOptions,
): Record<string, string | string[]> {
  const allFields = contractDef?.posting_requirements ?? [];
  const result: Record<string, string | string[]> = {};
  for (const field of allFields) {
    if (NON_INPUT_TYPES.has(field.type)) continue;
    if (!shouldShowField(field, rawValues, allFields, loadedOptions)) continue;
    const value = rawValues[field.name];
    const isFilled = Array.isArray(value)
      ? value.length > 0
      : value != null && String(value).trim() !== '';
    if (isFilled) result[field.name] = value as string | string[];
  }
  return result;
}

interface UseJobPostingFormProps {
  setPostingResult: Dispatch<SetStateAction<PostingResultData | null>>;
  setCurrentStep: Dispatch<SetStateAction<JobPostingStep>>;
  /** Set by JobPostingContext when `source_post_job:flow_start` fires. */
  flowStartedAtRef: MutableRefObject<number | null>;
}

function secondsSince(startedAt: number | null): number | undefined {
  return startedAt === null
    ? undefined
    : Math.round((Date.now() - startedAt) / 1000);
}

function uniqueChannelSlugs(
  purchases: readonly { sourcing_channel_slug: string }[],
): string[] {
  return Array.from(new Set(purchases.map(p => p.sourcing_channel_slug)));
}

/**
 * Hook for the job posting form. Returns the hook form, the is submitting state, and the handle post job function.
 */
export function useJobPostingForm({
  setPostingResult,
  setCurrentStep,
  flowStartedAtRef,
}: UseJobPostingFormProps) {
  const intl = useIntl();
  const { notifyError } = useToaster();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const isFirstJobPostOnAccount = useIsFirstJobPostOnAccount();
  const [isZipRecruiterSelected, setIsZipRecruiterSelected] = useState(false);
  const [vonqSelectedOpeningIds, setVonqSelectedOpeningIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const validationSchema = useValidationSchema(
    isZipRecruiterSelected,
    vonqSelectedOpeningIds,
  );
  const { calculateTotalBudget } = useBudgetType();

  const hookForm = useForm<JobPostingForm>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  /**
   * Mirror two pieces of channel-derived state into local state so the schema
   * rebuilds reactively:
   * 1. Whether ZipRecruiter is selected (drives stateCode validation).
   * 2. The set of opening ids that appear in any Vonq channel's `openingBudgets`
   *    (drives required-validation for the server-managed job-detail fields).
   */
  useEffect(() => {
    const subscription = hookForm.watch((values, { name }) => {
      if (name && !name.startsWith('channels')) return;

      const channels = Object.values(values.channels ?? {});

      const nextZipRecruiter = channels.some(
        channel => channel?.slug === ZIPRECRUITER,
      );
      setIsZipRecruiterSelected(prev =>
        prev === nextZipRecruiter ? prev : nextZipRecruiter,
      );

      const nextVonqOpeningIds = new Set<string>();
      channels.forEach(channel => {
        if (!channel?.vonqDetails || !channel.openingBudgets) return;
        Object.keys(channel.openingBudgets).forEach(openingId =>
          nextVonqOpeningIds.add(openingId),
        );
      });
      setVonqSelectedOpeningIds(prev => {
        if (
          prev.size === nextVonqOpeningIds.size &&
          Array.from(prev).every(id => nextVonqOpeningIds.has(id))
        ) {
          return prev;
        }
        return nextVonqOpeningIds;
      });
    });
    return () => subscription.unsubscribe();
  }, [hookForm]);

  const { getValues, handleSubmit } = hookForm;
  const lastRequestBodyRef = useRef<SourcingPurchaseBulkCreateRequest | null>(
    null,
  );
  // The flow start this hook last sent posting_complete for. A retry after a
  // failed post is the same attempt, so the funnel counts it once.
  const postingCompleteSentForRef = useRef<number | null | undefined>(
    undefined,
  );

  /* Post job by calling the bulk create API */
  const { mutate: postJob, isPending: isSubmitting } = useMutation({
    mutationFn: (requestBody: SourcingPurchaseBulkCreateRequest) =>
      SourcingService.postInternalApiSourcingSourcingPurchasesBulkCreate(
        requestBody,
      ),
    onSuccess: data => {
      const formData = getValues();
      const requestBody = lastRequestBodyRef.current;

      if (requestBody) {
        const result = derivePostingResult(formData, requestBody, data);
        setPostingResult(result);
      }

      postingCompleteSentForRef.current = undefined;
      setCurrentStep('result');
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to create job posting',
          description: 'Error message for job posting creation',
        }),
      });
    },
  });

  const buildPoolAttributes = (
    openingId: string,
    channelData: ChannelFormState,
  ): PoolAttributes => {
    // poolDetails is always set when slug === POOL (enforced by buildPoolDefaults)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const poolDetails = channelData.poolDetails!;
    const bodyHtml = poolDetails.openingMessages[openingId].body;
    const plainTextBody = stripHtmlTags(bodyHtml).trim();

    return {
      campaign_type: poolDetails.campaignType,
      from_email: poolDetails.fromEmail,
      is_never_ending: poolDetails.isNeverEnding,
      audience_id: poolDetails.openingMessages[openingId].audienceId,
      subject: poolDetails.openingMessages[openingId].subject,
      body: bodyHtml,
      plain_text_body: plainTextBody.length > 0 ? plainTextBody : null,
      buttons: poolDetails.openingMessages[openingId].buttons,
    };
  };

  const handlePostJob = handleSubmit(() => {
    const formData = getValues();

    // Regular channels (non-pool)
    const regularPurchases = Object.values(formData.channels)
      .filter(ch => ch.slug !== POOL)
      .flatMap(channelData =>
        Object.entries(channelData.openingBudgets)
          .filter(([_, openingBudget]) => openingBudget !== null)
          .map(([openingId, openingBudget]) => {
            const contractId = channelData.vonqDetails?.contractId ?? null;
            const contractDef =
              contractId != null
                ? formData.jobDetails[
                    openingId
                  ]?.vonqPostingRequirementsDefs?.find(
                    d => d.contract_id === contractId,
                  )
                : undefined;
            const loadedOptions = contractDef
              ? buildLoadedOptionsFromCache(contractDef, queryClient)
              : {};

            return {
              funnel_external_id: openingId,
              sourcing_channel_slug: channelData.slug,
              budget: openingBudget ?? 0,
              total_budget: calculateTotalBudget({
                budgetType: channelData.budgetType,
                budgetPerUnit: openingBudget ?? 0,
                startDate: channelData.startsAt,
                endDate: channelData.endAt,
              }),
              starts_at: channelData.startsAt,
              end_at: channelData.endAt,
              posting_title: formData.jobDetails[openingId].title,
              budget_type: channelData.budgetType,
              end_at_type: channelData.endAtType,
              pool_attributes: null,
              vonq_attributes: channelData.vonqDetails
                ? {
                    product_id: channelData.vonqDetails.productId,
                    remote_channel_id: channelData.vonqDetails.channelId,
                    channel_name: channelData.vonqDetails.channelName,
                    channel_type: channelData.vonqDetails.channelType,
                    logo_square_url: channelData.vonqDetails.logoSquareUrl,
                    logo_rectangle_url:
                      channelData.vonqDetails.logoRectangleUrl,
                    title: channelData.vonqDetails.title,
                    time_to_process:
                      channelData.vonqDetails.processingTime ?? null,
                    contract_id: contractId,
                    posting_requirements:
                      contractId != null
                        ? buildVonqPostingRequirementsPayload(
                            formData.jobDetails[openingId]
                              ?.vonqPostingRequirements[contractId] ?? {},
                            contractDef,
                            loadedOptions,
                          )
                        : {},
                  }
                : null,
            };
          }),
      );

    // Pool channel
    const poolChannel = Object.values(formData.channels).find(
      ch => ch.slug === POOL,
    );
    const poolPurchases = poolChannel?.poolDetails
      ? Object.entries(poolChannel.poolDetails.openingMessages).map(
          ([openingId]) => ({
            funnel_external_id: openingId,
            sourcing_channel_slug: POOL,
            budget: 0,
            total_budget: 0,
            posting_title: formData.jobDetails[openingId].title,
            budget_type: null,
            end_at_type: null,
            starts_at: poolChannel.startsAt,
            end_at: poolChannel.poolDetails?.isNeverEnding
              ? null
              : poolChannel.endAt,
            pool_attributes: buildPoolAttributes(openingId, poolChannel),
            vonq_attributes: null,
          }),
        )
      : [];

    const timeToPostSeconds = secondsSince(flowStartedAtRef.current);
    // Hire sends `source_job_post:posting_publish` once every campaign is
    // created, so the flow's mode and duration ride along in the request.
    const requestBody: SourcingPurchaseBulkCreateRequest = {
      sourcing_purchases: [...regularPurchases, ...poolPurchases],
      post_mode: 'Manual',
      time_to_post_seconds: timeToPostSeconds,
    };

    lastRequestBodyRef.current = requestBody;

    if (postingCompleteSentForRef.current !== flowStartedAtRef.current) {
      postingCompleteSentForRef.current = flowStartedAtRef.current;
      capturePostingComplete(requestBody, timeToPostSeconds);
    }

    postJob(requestBody);
  });

  function capturePostingComplete(
    requestBody: SourcingPurchaseBulkCreateRequest,
    timeToPostSeconds: number | undefined,
  ) {
    const purchases = requestBody.sourcing_purchases;
    posthog.capture('source_job_post:posting_complete', {
      openings_selected: Array.from(
        new Set(purchases.map(p => p.posting_title)),
      ),
      openings_id: Array.from(
        new Set(purchases.map(p => p.funnel_external_id)),
      ),
      post_mode: 'Manual',
      channels_selected: uniqueChannelSlugs(purchases),
      campaigns_count: purchases.length,
      budget_allocated: purchases.reduce(
        (sum, p) => sum + (p.total_budget ?? 0),
        0,
      ),
      currency: SOURCING_CURRENCY,
      pay_type: SOURCING_PAY_TYPE,
      is_marketplace_board_selected: purchases.some(
        p => p.vonq_attributes != null,
      ),
      // The flow posts existing openings only; it has no way to create one.
      is_new_opening: false,
      is_first_job_post_on_account: isFirstJobPostOnAccount,
      time_to_post_seconds: timeToPostSeconds,
    });
  }

  return { hookForm, isSubmitting, handlePostJob };
}
